import type { RequestResult } from '@trellis/shared'

import { env } from '@/env'
import { getStoredTenantSlug } from '@/lib/tenant'

export interface DevSettingsResponse {
  emailDivertOverrideEnabled: boolean
  emailDivertOverrideTo: string | null
  emailSandboxEnabled: boolean
}

export interface UpdateDevSettingsRequest {
  emailDivertOverrideEnabled: boolean
  emailDivertOverrideTo: string | null
  emailSandboxEnabled: boolean
}

export async function getDevSettings(): Promise<RequestResult<DevSettingsResponse>> {
  const headers = new Headers()
  const slug = getStoredTenantSlug()
  if (slug) headers.set('X-Tenant-Slug', slug)

  try {
    const response = await fetch(`${env.VITE_API_BASE_URL}/api/dev-settings`, {
      credentials: 'include',
      headers,
    })
    const body = await response.json()

    if (!response.ok) {
      return {
        status: response.status,
        data: null,
        pagination: null,
        error: {
          code: body.code ?? 'API_ERROR',
          message: body.message ?? 'Failed to load dev settings',
        },
      }
    }

    return { status: response.status, data: body.data, pagination: null, error: null }
  } catch (caughtError) {
    return {
      status: 0,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : 'Failed to connect to the server',
      },
    }
  }
}

/**
 * Always returns a 500 from the server — the endpoint deliberately throws so the request
 * exercises the same GlobalExceptionFilter → Sentry.captureException path as a real
 * production error. That 500 is the expected, successful outcome here.
 */
export async function triggerTestError(): Promise<RequestResult<null>> {
  const headers = new Headers()
  const slug = getStoredTenantSlug()
  if (slug) headers.set('X-Tenant-Slug', slug)

  try {
    const response = await fetch(`${env.VITE_API_BASE_URL}/api/dev-settings/test-error`, {
      method: 'POST',
      credentials: 'include',
      headers,
    })

    if (response.status === 500) {
      return { status: response.status, data: null, pagination: null, error: null }
    }

    const body = await response.json()
    return {
      status: response.status,
      data: null,
      pagination: null,
      error: {
        code: body.error?.code ?? 'API_ERROR',
        message: body.error?.message ?? 'Failed to trigger test error',
      },
    }
  } catch (caughtError) {
    return {
      status: 0,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : 'Failed to connect to the server',
      },
    }
  }
}

/**
 * Clears the tenant's onboarding-celebration flag and the current user's dismissed
 * onboarding cards. A 204 with no body is the expected success outcome.
 */
export async function resetOnboarding(): Promise<RequestResult<null>> {
  const headers = new Headers()
  const slug = getStoredTenantSlug()
  if (slug) headers.set('X-Tenant-Slug', slug)

  try {
    const response = await fetch(
      `${env.VITE_API_BASE_URL}/api/dev-settings/reset-onboarding`,
      { method: 'POST', credentials: 'include', headers },
    )

    if (response.status === 204) {
      return { status: response.status, data: null, pagination: null, error: null }
    }

    const body = await response.json()
    return {
      status: response.status,
      data: null,
      pagination: null,
      error: {
        code: body.code ?? 'API_ERROR',
        message: body.message ?? 'Failed to reset onboarding state',
      },
    }
  } catch (caughtError) {
    return {
      status: 0,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : 'Failed to connect to the server',
      },
    }
  }
}

export async function updateDevSettings(
  data: UpdateDevSettingsRequest,
): Promise<RequestResult<DevSettingsResponse>> {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const slug = getStoredTenantSlug()
  if (slug) headers.set('X-Tenant-Slug', slug)

  try {
    const response = await fetch(`${env.VITE_API_BASE_URL}/api/dev-settings`, {
      method: 'PATCH',
      credentials: 'include',
      headers,
      body: JSON.stringify(data),
    })
    const body = await response.json()

    if (!response.ok) {
      return {
        status: response.status,
        data: null,
        pagination: null,
        error: {
          code: body.code ?? 'API_ERROR',
          message: body.message ?? 'Failed to update dev settings',
        },
      }
    }

    return { status: response.status, data: body.data, pagination: null, error: null }
  } catch (caughtError) {
    return {
      status: 0,
      data: null,
      pagination: null,
      error: {
        code: 'NETWORK_ERROR',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : 'Failed to connect to the server',
      },
    }
  }
}
